import {t} from "i18next";
import {isEmpty} from "lodash-es";
import omit from "lodash-es/omit";
import {all, call, delay, put, select, takeLatest} from "redux-saga/effects";
import {selectCurrentUserId} from "store/selectors";
import {
  fetchIncidents,
  fetchIncidentsCount,
  fetchNewIncidentsCount,
  fetchOpenIncidentsCount,
  fetchUnassignedIncidentsCount,
  linkIncidentsAPI,
  patchIncidentStatus,
  setBulkIncidentDelete
} from "../../api/incidents";
import {fetchUserRegistrySettings} from "../../api/registrySettings";
import {
  buildAssigneeQuery,
  buildStatusQuery,
  getAPIErrorMessages
} from "../../api/utils/incidents";
import {getIncidentManagerSettings} from "../../config/localStorage";
import {FieldNames} from "../../models/fields";
import {
  handleRemovedFilterSortOptions,
  UISettingsKeyForIncidentManager,
  updateIncidentManagerSettings
} from "../../models/filter";
import {INCIDENT_FILTER_SORT_DEFAULT} from "../../models/options";
import {authFlowComplete} from "../features/authSlice";
import {
  getFilterSortSettings,
  getFilterSortSettingsFailure,
  getFilterSortSettingsSuccess,
  updateFilterSortSettingsFailure,
  updateFilterSortSettingsSuccess
} from "../features/incidentSettingsSlice";
import {
  bulkDeleteIncidents,
  bulkDeleteIncidentsFailure,
  bulkDeleteIncidentsSuccess,
  getIncidentsCount,
  getIncidentsCountFailure,
  getIncidentsCountSuccess,
  getNewIncidentsCountFailure,
  getNewIncidentsCountSuccess,
  getOpenIncidentsCountFailure,
  getOpenIncidentsCountSuccess,
  getUnassignedIncidentsCountFailure,
  getUnassignedIncidentsCountSuccess,
  resetIncidentList,
  searchIncidents,
  searchIncidentsFailure,
  searchIncidentsSuccess,
  setIncidentStatus,
  setIncidentStatusFailure,
  setIncidentStatusSuccess,
  setLinkingIncidentFailure,
  setLinkingIncidents,
  setLinkingIncidentsSuccess,
  setLoadingIncidents
} from "../features/incidentsSlice";
import {
  setErrorNotification,
  setSuccessNotification
} from "../features/notificationsSlice";
import {
  filterDeletedUsers,
  getDateRangeQueryValues,
  getSearchErrorMessage,
  takeUserList
} from "./utils";

export function* getIncidentsCountSaga() {
  try {
    const count = yield call(fetchIncidentsCount);
    yield put(getIncidentsCountSuccess(count));
  } catch (error) {
    console.error(error);
    yield put(getIncidentsCountFailure(error));
  }
}

export function* getUnassignedIncidentsCountSaga() {
  try {
    const count = yield call(fetchUnassignedIncidentsCount);
    yield put(getUnassignedIncidentsCountSuccess(count));
  } catch (error) {
    console.error(error);
    yield put(getUnassignedIncidentsCountFailure(error));
  }
}

export function* getOpenIncidentsCountSaga() {
  try {
    const count = yield call(fetchOpenIncidentsCount);
    yield put(getOpenIncidentsCountSuccess(count));
  } catch (error) {
    console.error(error);
    yield put(getOpenIncidentsCountFailure(error));
  }
}

export function* getNewIncidentsCountSaga() {
  try {
    const count = yield call(fetchNewIncidentsCount);
    yield put(getNewIncidentsCountSuccess(count));
  } catch (error) {
    console.error(error);
    yield put(getNewIncidentsCountFailure(error));
  }
}

export function* getAllIncidentCountsSaga() {
  yield all([
    call(getIncidentsCountSaga),
    call(getNewIncidentsCountSaga),
    call(getOpenIncidentsCountSaga),
    call(getUnassignedIncidentsCountSaga)
  ]);
}

export function* getIncidentSettingsSaga() {
  try {
    // Check if local storage has incident-manager-settings (UISettingsKeyForIncidentManager)
    const settingsFromLocalStorage = handleRemovedFilterSortOptions(
      yield getIncidentManagerSettings()
    );

    // If there's nothing in local storage, check if user registry has settings
    if (!settingsFromLocalStorage || isEmpty(settingsFromLocalStorage)) {
      try {
        // If settings exist in user registry, update local storage
        const settingsFromRegistry = yield fetchUserRegistrySettings(
          UISettingsKeyForIncidentManager
        );
        if (settingsFromRegistry) {
          yield call(
            updateIncidentManagerSettings,
            handleRemovedFilterSortOptions({
              ...INCIDENT_FILTER_SORT_DEFAULT,
              ...settingsFromLocalStorage
            })
          );
        }
      } catch (error) {
        // If there's no settings, set settings to default
        yield call(updateIncidentManagerSettings, {
          ...INCIDENT_FILTER_SORT_DEFAULT
        });
      }
    }
    yield put(
      getFilterSortSettingsSuccess({
        ...INCIDENT_FILTER_SORT_DEFAULT,
        ...settingsFromLocalStorage
      })
    );
  } catch (error) {
    console.error(error);
    yield put(getFilterSortSettingsFailure(error));
    yield put(
      setErrorNotification(t("incidents.messages.filterSortSettingsError"))
    );
  }
}

export function* searchIncidentsSaga(action) {
  try {
    const {
      assigneeFilter,
      dateRangeFilter,
      page,
      searchText,
      orderBy,
      order,
      statusFilter
    } = action.payload;
    const settingsDisabled =
      searchText.length > 0 && searchText.includes("id:incident");

    const currentUserId = yield select(selectCurrentUserId);
    const assigneeQuery = buildAssigneeQuery(assigneeFilter, currentUserId);
    const statusQuery = buildStatusQuery(statusFilter);
    const dateRangeQuery = getDateRangeQueryValues(dateRangeFilter);
    const fetchIncidentsParams = getFetchIncidentsParams(
      {
        ...action.payload,
        assigneeQuery,
        statusQuery,
        dateRangeQuery,
        highImpact: true
      },
      settingsDisabled
    );

    const incidents = yield call(fetchIncidents, fetchIncidentsParams);
    const users = yield* takeUserList();
    const filteredDataRows = incidents.dataRows.map((row) =>
      filterDeletedUsers(users, row)
    );
    const filteredIncidents = {...incidents, dataRows: filteredDataRows};

    yield put(
      searchIncidentsSuccess({
        assigneeFilter,
        assigneeQuery,
        dateRangeFilter,
        incidents: filteredIncidents,
        page,
        searchText,
        settingsDisabled,
        orderBy,
        order,
        statusFilter,
        statusQuery
      })
    );
  } catch (error) {
    console.error(error);
    yield put(searchIncidentsFailure(error));
    yield put(
      setErrorNotification(
        getSearchErrorMessage(error, t("incidents.messages.listLoadingError"))
      )
    );
  }
}

// Updates user settings each time searchIncidents fires
export function* updateIncidentSettingsSaga(action) {
  try {
    const {orderBy, order, dateRangeFilter, assigneeFilter, statusFilter} =
      action.payload;

    const currentUserId = yield select(selectCurrentUserId);
    const assigneeQuery = buildAssigneeQuery(assigneeFilter, currentUserId);
    const statusQuery = buildStatusQuery(statusFilter);

    const settings = {
      order,
      orderBy: orderBy,
      assigneeQuery: assigneeQuery,
      statusQuery: statusQuery,
      dateRangeFilter: dateRangeFilter,
      assigneeFilter: assigneeFilter,
      statusFilter: statusFilter
    };

    yield call(updateIncidentManagerSettings, settings);
    yield put(updateFilterSortSettingsSuccess(settings));
  } catch (error) {
    console.error(error);
    yield put(updateFilterSortSettingsFailure(error));
  }
}

function getFetchIncidentsParams(defaults, settingsDisabled) {
  return omit(
    defaults,
    settingsDisabled && ["statusQuery", "assigneeQuery", "dateRangeQuery"]
  );
}

export function* setIncidentStatusSaga(action) {
  try {
    const {incidents, status} = action.payload;
    yield call(patchIncidentStatus, incidents, status);
    yield put(
      setSuccessNotification(t("incidents.messages.statusUpdateSuccess"))
    );
    incidents.forEach((i) => {
      i.oldStatus = i.status;
      i.status = status;
    });
    yield put(setIncidentStatusSuccess({incidents}));
  } catch (error) {
    console.error(error);
    yield put(setIncidentStatusFailure(error));
    yield put(setErrorNotification(t("incidents.messages.statusUpdateError")));
  }
}

export function* bulkDeleteIncidentsSaga(action) {
  try {
    const response = yield call(setBulkIncidentDelete, {
      incidents: action.payload
    });
    const deletedIncidentIds = response.data.incidents?.deleted;
    const deletedIncidentCount = deletedIncidentIds?.length || 0;
    yield put(bulkDeleteIncidentsSuccess(deletedIncidentIds));
    yield put(
      setSuccessNotification(
        t("incidents.messages.deletedIncidentsSuccess", {
          count: deletedIncidentCount
        })
      )
    );
    yield put(setLoadingIncidents(true)); // Manually set to avoid flash during delay
    yield delay(1000);
    yield all([put(getIncidentsCount()), put(resetIncidentList())]);
  } catch (error) {
    yield put(setErrorNotification(getAPIErrorMessages(error)));
    yield put(bulkDeleteIncidentsFailure(error));
  }
}

export function* linkingIncidentsSaga(action) {
  try {
    const {target, sources} = action.payload;
    const currentUserId = yield select(selectCurrentUserId);

    const responses = yield all(
      sources.map((source) =>
        call(linkIncidentsAPI, {
          type: FieldNames.relationship,
          source_ref: source,
          target_ref: target,
          relationship_type: FieldNames.duplicateOf,
          source: "Incident Manager",
          title: "Linked Incident",
          short_description: `These incidents were linked in CTR by ${currentUserId}`
        })
      )
    );

    yield put(setLinkingIncidentsSuccess(responses));
    yield put(resetIncidentList());
  } catch (e) {
    yield put(setLinkingIncidentFailure(e));
  }
}

export default function* incidentsSagas() {
  yield all([
    yield takeLatest(
      [authFlowComplete, getFilterSortSettings],
      getIncidentSettingsSaga
    ),
    yield takeLatest(getIncidentsCount, getAllIncidentCountsSaga),
    yield takeLatest(searchIncidents, searchIncidentsSaga),
    yield takeLatest(searchIncidents, updateIncidentSettingsSaga),
    yield takeLatest(setIncidentStatus, setIncidentStatusSaga),
    yield takeLatest(bulkDeleteIncidents, bulkDeleteIncidentsSaga),
    yield takeLatest(setLinkingIncidents, linkingIncidentsSaga)
  ]);
}
